import { BaseApiClient } from './base-client';

/**
 * Model status as returned from the API
 */
export interface ModelStatus {
  id: number;
  modelId: string;
  modelType: 'chat' | 'image' | 'video';
  statusType: string;
  customText: string | null;
  severity: 'info' | 'warning' | 'critical';
  showInSelector: boolean;
  showInComposer: boolean;
  isActive: boolean;
  expiresAt: string | null;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Params for creating/updating a model status
 */
export interface UpsertModelStatusParams {
  modelId: string;
  modelType: 'chat' | 'image' | 'video';
  statusType: string;
  customText?: string;
  severity?: 'info' | 'warning' | 'critical';
  showInSelector?: boolean;
  showInComposer?: boolean;
  isActive?: boolean;
  expiresInHours?: number;
}

/**
 * Model Statuses API — public + admin endpoints
 */
export class ModelStatusesApi extends BaseApiClient {

  /**
   * Get all active model statuses (public endpoint)
   */
  async getActiveStatuses(): Promise<ModelStatus[]> {
    try {
      const response = await this.client.get('/api/model-statuses');
      return response.data.statuses || [];
    } catch (error: any) {
      console.error('Error fetching model statuses:', error);
      return [];
    }
  }

  /**
   * Get all statuses (admin endpoint, includes inactive/expired)
   */
  async getAllStatuses(): Promise<ModelStatus[]> {
    try {
      const response = await this.client.get('/api/admin/model-statuses');
      return response.data.statuses || [];
    } catch (error: any) {
      console.error('Error fetching admin model statuses:', error);
      return [];
    }
  }

  /**
   * Create or update a model status (admin endpoint)
   */
  async upsertStatus(params: UpsertModelStatusParams): Promise<ModelStatus | null> {
    try {
      const response = await this.client.post('/api/admin/model-statuses', params);
      if (response.data.success) {
        return response.data.status;
      }
      return null;
    } catch (error: any) {
      console.error('Error upserting model status:', error);
      throw new Error(error.response?.data?.error || 'Не удалось сохранить статус');
    }
  }

  /**
   * Delete a model status (admin endpoint)
   */
  async deleteStatus(id: number): Promise<void> {
    try {
      await this.client.delete(`/api/admin/model-statuses/${id}`);
    } catch (error: any) {
      console.error('Error deleting model status:', error);
      throw new Error('Не удалось удалить статус');
    }
  }
}
